'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in the Air at Home: HEPA, Carpets & Indoor Sources"
      author="MicroPlastics Editorial Team"
      date="May 13, 2026"
      readTime="10 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics in indoor air at home, sources and HEPA solutions"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Indoor air contains <strong>3–15× more microplastics than outdoor air</strong>, a finding consistent across studies in Paris, London, Shanghai, and
        Aarhus. The dominant sources are <strong>synthetic carpet and rug
        fibres</strong>, <strong>polyester clothing and bedding</strong>, and
        <strong> household dust</strong> made up substantially of textile
        microfibres. The average person inhales an estimated <strong>up to
        70,000 microplastic particles per year</strong> indoors. The four
        highest-impact reductions: HEPA filtration in the bedroom, natural-fibre
        bedding and clothing, hard flooring or natural-fibre rugs, and
        regular damp-cloth dusting (not dry sweeping which redistributes particles).
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Indoor microplastic concentrations average 3–15× higher than outdoor air across all sampled cities.</>,
          <>Synthetic carpets (nylon, polyester, polypropylene) are the largest single source of indoor airborne microplastic.</>,
          <>Polyester clothing and bedding release microfibres into bedroom air both day and night.</>,
          <>Vrey et al. and others have estimated <strong>up to 70,000 microplastic particles inhaled annually</strong> in a typical home.</>,
          <>A HEPA air purifier rated for the room size + natural-fibre textiles + damp dusting + hard flooring eliminates 70%+ of indoor airborne exposure.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why indoor air is worse than outdoor
      </h2>
      <p>
        It seems counter-intuitive, but multiple independent studies have
        confirmed it: indoor air is consistently 3 to 15 times more contaminated
        with microplastic particles than the outdoor air nearby. The reasons:
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>You bring sources inside.</strong> Synthetic carpets, polyester
          clothing, plastic furniture coatings, vinyl flooring, and synthetic
          curtains all shed particles constantly.
        </li>
        <li>
          <strong>Limited ventilation.</strong> A typical air exchange is once
          per 2–4 hours in modern insulated homes. Outdoor air dilutes
          particles fast; indoor air accumulates them.
        </li>
        <li>
          <strong>You stir them up.</strong> Walking, vacuuming, getting dressed,
          and making the bed all re-suspend particles that have settled.
        </li>
        <li>
          <strong>Skin-cell shedding bonds with textile fibres.</strong> Household
          dust is on average 30%+ textile fibres in modern homes, much of it
          synthetic.
        </li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Where indoor microplastic comes from
      </h2>

      <BlogComparisonTable
        caption="Major indoor microplastic sources by relative contribution"
        columns={[
          { key: 'source', label: 'Source' },
          { key: 'share', label: 'Relative contribution' },
          { key: 'why', label: 'Why' },
        ]}
        rows={[
          { source: 'Synthetic carpets and rugs', share: 'Largest', why: 'Constant friction releases nylon, polyester, polypropylene fibres' },
          { source: 'Polyester clothing and bedding', share: 'Large', why: 'Worn or slept on for hours daily; sheds with each movement' },
          { source: 'Synthetic curtains and upholstery', share: 'Moderate', why: 'Static exposure to airflow and sunlight degradation' },
          { source: 'Plastic-based dust (general)', share: 'Moderate', why: 'Composed largely of textile and packaging fragments' },
          { source: 'Vinyl flooring (LVT, linoleum-style)', share: 'Moderate', why: 'Surface wear from foot traffic' },
          { source: 'Microwave / cooking activities', share: 'Localised', why: 'Plastic heating bursts; localised kitchen exposure' },
          { source: 'HVAC system biofilm + plastic ducts', share: 'Variable', why: 'Some plastic duct materials shed; filter quality matters' },
          { source: 'Painted walls and ceilings (acrylic paints)', share: 'Slow / chronic', why: 'Paint dust over years' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The HEPA solution
      </h2>
      <p>
        True HEPA (High-Efficiency Particulate Air) filters are rated to remove
        99.97% of particles at 0.3 µm. Most airborne microplastic particles are
        between 0.5 µm and 200 µm, putting them well within HEPA range, though
        nanoplastics (&lt;1 µm) are harder to capture.
      </p>
      <p>Buying guidelines:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Look for <strong>True HEPA</strong> or <strong>H13 / H14 HEPA</strong> labelling, not just &ldquo;HEPA-type&rdquo;.</li>
        <li>Choose a unit with a <strong>CADR rating</strong> (Clean Air Delivery Rate) at least 2/3 of the room&apos;s square footage.</li>
        <li>Run it <strong>24/7 in bedrooms</strong>, you sleep 7-9 hours in one place, perfect for cumulative filtering.</li>
        <li>Avoid &ldquo;ionic&rdquo; or &ldquo;plasmacluster&rdquo; air cleaners as a standalone, these don&apos;t capture particles, just neutralise some chemistries.</li>
        <li>Well-regarded brands: Coway, Levoit, Blueair, IQAir, Austin Air, Winix.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The full home-air reduction protocol
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>HEPA filter in every bedroom</strong>, running 24/7. Replace
          filter cartridges per manufacturer schedule (typically every 6–12 months).
        </li>
        <li>
          <strong>Replace synthetic bedding with cotton, linen, or wool.</strong>
          You spend a third of your life on bedding, so this one counts.
        </li>
        <li>
          <strong>Phase out polyester clothing</strong> over time, cotton, linen,
          wool, silk, hemp, Tencel. Especially activewear (which sheds the most
          due to friction and sweat).
        </li>
        <li>
          <strong>Hard flooring beats carpet</strong> for microplastic exposure.
          Hardwood, tile, natural-cork, or natural-stone floors with washable
          rugs (cotton, wool, jute).
        </li>
        <li>
          <strong>Damp-dust weekly</strong> with a microfibre cloth used wet.
          Dry dusting and feather dusters redistribute particles into the air.
        </li>
        <li>
          <strong>Vacuum with a HEPA-equipped vacuum.</strong> Standard bags
          re-emit fine particles into the air. Brands: Miele, Shark with HEPA
          option, Dyson.
        </li>
        <li>
          <strong>Open windows for at least 10 minutes daily</strong> for air
          exchange, outdoor air is cleaner for microplastics.
        </li>
        <li>
          <strong>Avoid synthetic air fresheners.</strong> Aerosol fresheners
          release VOCs and may release packaging-derived particles.
        </li>
        <li>
          <strong>Replace HVAC filters with MERV 13+</strong> if your system
          accepts them. Many central HVAC systems can take HEPA-equivalent
          filters with minor modifications.
        </li>
      </ol>

      <p>
        See related:{' '}
        <Link href="/blog/airborne-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          airborne microplastics deep dive
        </Link>,{' '}
        <Link href="/blog/microplastics-in-clothing" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in clothing
        </Link>, and{' '}
        <Link href="/blog/microplastics-in-your-home" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in your home
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Material, stainless, glass, ceramic, cast iron, plastic (PE / PP / PS / PVC), silicone, wood.</>,
          <>Visible condition, scratches, chips, warping, fade.</>,
          <>Brand and product line, flags for non-stick / PFAS-treated items.</>,
          <>Use-context flags you log, heat exposure, dishwasher cycles, contact with hot or fatty food.</>,
          <>Cited published research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan kitchen and household products before buying"
        sub="Cookware, food storage, cutting boards, accessories. The app weighs material, condition, brand, and use-context to give a 0–100 risk score per item."
        buttonLabel="Scan household items in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Is indoor air really worse than outdoor air for microplastics?',
            answer:
              'Yes, consistently across studies. Indoor microplastic concentrations average 3-15× higher than outdoor air. The major sources are synthetic carpets, polyester clothing and bedding, and household dust comprised substantially of textile fibres.',
          },
          {
            question: 'Does a HEPA filter remove microplastics from indoor air?',
            answer:
              'Yes for particles down to 0.3 µm. True HEPA captures 99.97% at that size. Most airborne microplastic particles are 0.5-200 µm, well within HEPA range. Nanoplastics (<1 µm) are harder to capture but partially filtered.',
          },
          {
            question: 'What is the best air purifier for microplastics?',
            answer:
              'Look for True HEPA or H13/H14 HEPA certification with a CADR rating at least 2/3 of your room square footage. Well-regarded brands: Coway Mighty, Levoit Core 600S, Blueair Pure 411, IQAir HealthPro, Austin Air HealthMate.',
          },
          {
            question: 'How much plastic do you breathe in indoors?',
            answer:
              'Estimates vary by study, but typical figures range from 26,000 to 130,000+ microplastic particles inhaled per year in average indoor environments. A 2019 study in Environmental Science & Technology placed the figure around 70,000 per year for the average Western adult.',
          },
          {
            question: 'Does carpet release microplastics?',
            answer:
              'Yes, significantly. Synthetic carpets (nylon, polyester, polypropylene) are the largest single source of indoor airborne microplastic. Foot traffic and vacuuming both release fibres into the air. Hard flooring with natural-fibre rugs is the safer choice.',
          },
          {
            question: 'Does vacuuming reduce or increase microplastic exposure?',
            answer:
              'A HEPA-equipped vacuum reduces it by capturing particles. A standard vacuum can increase short-term exposure by re-suspending fine particles into the air through the exhaust. Always use HEPA-filtered vacuums (Miele, Shark with HEPA, Dyson).',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Vianello A, Jensen RL, Liu L, Vollertsen J',
            year: 2019,
            title: 'Simulating human exposure to indoor airborne microplastics using a Breathing Thermal Manikin',
            publisher: 'Scientific Reports',
            url: 'https://www.nature.com/articles/s41598-019-45054-w',
          },
          {
            authors: 'Dris R, Gasperi J, Mirande C, et al.',
            year: 2017,
            title: 'A first overview of textile fibers, including microplastics, in indoor and outdoor environments',
            publisher: 'Environmental Pollution',
            url: 'https://www.sciencedirect.com/science/article/abs/pii/S0269749116307552',
          },
          {
            authors: 'Liao Z, Ji X, Ma Y, et al.',
            year: 2021,
            title: 'Airborne microplastics in indoor and outdoor environments of a coastal city in Eastern China',
            publisher: 'Journal of Hazardous Materials',
            url: 'https://www.sciencedirect.com/science/article/abs/pii/S0304389421008888',
          },
          {
            authors: 'World Health Organization',
            year: 2022,
            title: 'Dietary and inhalation exposure to nano- and microplastic particles and potential implications for human health',
            publisher: 'WHO',
            url: 'https://www.who.int/publications/i/item/9789240054608',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
